<script lang="ts">
  import type { FormComponentSlotProps } from "@inertiajs/core"
  import { Form } from "@inertiajs/svelte"
  import { LoaderCircle } from "@lucide/svelte"

  import InputError from "@/components/input-error.svelte"
  import { Button } from "@/components/ui/button"
  import { Input } from "@/components/ui/input"
  import { Label } from "@/components/ui/label"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { newTaskTemplatePath, taskTemplatesPath } from "@/routes"
  import type { BreadcrumbItem, User } from "@/types"

  interface Props {
    users: User[]
  }

  let { users }: Props = $props()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Task Templates",
      href: taskTemplatesPath(),
    },
    {
      title: "New Template",
      href: newTaskTemplatePath(),
    },
  ]

  let minInstances = $state<number | undefined>(undefined)
  let maxInstances = $state<number | undefined>(undefined)
</script>

<svelte:head>
  <title>New Task Template - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div>
      <h1 class="text-3xl font-bold">New Task Template</h1>
      <p class="text-muted-foreground mt-2">Create a new chore template</p>
    </div>

    <div class="max-w-2xl">
      <Form
        method="post"
        action={taskTemplatesPath()}
        class="space-y-6"
      >
        {#snippet children({ processing, errors }: FormComponentSlotProps)}
          <div class="space-y-4">
            <div class="space-y-2">
              <Label for="name">Name *</Label>
              <Input id="name" name="name" type="text" required autofocus />
              <InputError message={errors.name} />
            </div>

            <div class="space-y-2">
              <Label for="description">Description</Label>
              <textarea
                id="description"
                name="description"
                class="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              ></textarea>
              <InputError message={errors.description} />
            </div>

            <div class="space-y-2" id="available_to_field">
              <Label for="available_to_id">Available To</Label>
              <select
                id="available_to_id"
                name="available_to_id"
                class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Any member</option>
                {#each users as user (user.id)}
                  <option value={user.id}>{user.name}</option>
                {/each}
              </select>
              <InputError message={errors.available_to_id} />
            </div>

            <div class="space-y-2">
              <Label for="frequency_type">Frequency Type *</Label>
              <select
                id="frequency_type"
                name="frequency_type"
                required
                class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="periodic">Periodic (recurring)</option>
                <option value="ad_hoc">Ad-hoc (on demand)</option>
              </select>
              <InputError message={errors.frequency_type} />
            </div>

            <div class="space-y-2">
              <Label>
                <input type="checkbox" name="requires_approval" value="1" />
                <span class="ml-2">Requires approval</span>
              </Label>
            </div>

            <div class="space-y-2">
              <Label for="payment_basis">Payment Basis *</Label>
              <select
                id="payment_basis"
                name="payment_basis"
                required
                class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="per_occurrence">Per Occurrence</option>
                <option value="per_period">Per Period</option>
              </select>
              <InputError message={errors.payment_basis} />
            </div>

            <div class="space-y-2">
              <Label for="wage">Wage *</Label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="wage"
                  name="wage"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                  class="pl-7"
                />
              </div>
              <InputError message={errors.wage} />
            </div>

            <div class="space-y-4">
              <Label>Task Limits (per week)</Label>
              <div class="grid gap-4 md:grid-cols-2">
                <div class="space-y-2">
                  <Label for="min_instances">Minimum Instances</Label>
                  <Input
                    id="min_instances"
                    name="min_instances"
                    type="number"
                    bind:value={minInstances}
                    min="1"
                    placeholder="Leave blank for no minimum"
                  />
                  <InputError message={errors.min_instances} />
                </div>
                <div class="space-y-2">
                  <Label for="max_instances">Maximum Instances</Label>
                  <Input
                    id="max_instances"
                    name="max_instances"
                    type="number"
                    bind:value={maxInstances}
                    min="1"
                    placeholder="Leave blank for no maximum"
                  />
                  <InputError message={errors.max_instances} />
                </div>
              </div>
            </div>

            <div class="flex gap-4">
              <Button type="submit" disabled={processing}>
                {#if processing}
                  <LoaderCircle class="h-4 w-4 animate-spin" />
                {/if}
                Create Template
              </Button>
              <Button type="button" variant="outline" href={taskTemplatesPath()}>
                Cancel
              </Button>
            </div>
          </div>
        {/snippet}
      </Form>
    </div>
  </div>
</AppLayout>
